import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { dubbedCourses } from '../data/mockData';
import { useLanguage } from '../context/LanguageContext';

const YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@International.Nawwaf";

const DubbedSection = () => {
  const scrollRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const { t } = useLanguage();
  
  // Auto-scroll functionality
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollInterval;
    
    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (!isPaused && scrollContainer) {
          const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
          
          if (scrollContainer.scrollLeft >= maxScroll - 10) {
            // Reset to beginning with smooth animation
            scrollContainer.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            scrollContainer.scrollBy({ left: 2, behavior: 'auto' });
          }
        }
      }, 30);
    };

    startAutoScroll();

    return () => {
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [isPaused]);

  const scroll = (direction) => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      const scrollAmount = 400;
      scrollContainer.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Handle card click - open YouTube channel
  const handleCardClick = () => {
    window.open(YOUTUBE_CHANNEL_URL, '_blank', 'noopener,noreferrer');
  };

  // Duplicate courses for seamless looping
  const extendedCourses = [...dubbedCourses, ...dubbedCourses];

  return (
    <section id="dubbed" className="py-16" style={{
      background: 'linear-gradient(180deg, #2d1a4a 0%, #251845 100%)'
    }}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {t('dubbedTitle')}
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {t('dubbedDesc')}
          </p>
        </div>
        
        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button 
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-[#2a2a4a]/80 backdrop-blur-sm flex items-center justify-center text-white hover:bg-pink-600 transition-all duration-300 -ml-4"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <button 
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-[#2a2a4a]/80 backdrop-blur-sm flex items-center justify-center text-white hover:bg-pink-600 transition-all duration-300 -mr-4"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Scrollable Container */}
          <div 
            ref={scrollRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {extendedCourses.map((course, index) => (
              <div 
                key={`${course.id}-${index}`}
                onClick={handleCardClick}
                className="flex-shrink-0 w-80 bg-[#1a1a2e]/70 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/10 group cursor-pointer"
              >
                <div className="relative">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-pink-600 text-white text-xs px-3 py-1 rounded-full font-medium">
                      {course.language}
                    </span>
                  </div>
                  {/* YouTube indicator on hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-red-600 rounded-full p-3">
                      <ExternalLink className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-1">{course.title}</h3>
                  <p className="text-gray-400 text-sm mb-2">{t('duration')}: {course.duration}</p>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{course.description}</p>
                  <Button className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white">
                    {t('startLearning')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {dubbedCourses.map((_, index) => (
            <div 
              key={index}
              className="w-2 h-2 rounded-full bg-gray-600"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DubbedSection;
